import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from '../redux/hooks';
import { RootState } from '../redux/store';

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  padding: 40px;
  background: #ffffff;
`;

const Title = styled.h1`
  font-family: 'IBM Plex Mono', monospace;
  font-size: 32px;
  color: #a13b16;
  text-align: center;
  margin-bottom: 30px;
`;

const HorizontalScroll = styled.div`
  display: flex;
  overflow-x: auto;
  gap: 20px;
  padding-bottom: 20px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
`;

const OrderCard = styled.div<{ $processed: boolean }>`
  padding: 20px;
  border: 1px solid #ccc;
  border-radius: 8px;
  background: ${props => props.$processed ? '#f5f5f5' : '#ffffff'};
  opacity: ${props => props.$processed ? 0.7 : 1};
  min-width: 280px;
`;

const OrderField = styled.p`
  margin: 5px 0;
  font-size: 14px;
`;

const ProcessButton = styled.button<{ outline?: boolean }>`
  padding: 8px 16px;
  background: ${props => props.outline ? 'transparent' : '#a13b16'};
  color: ${props => props.outline ? '#a13b16' : 'white'};
  border: none; /* прибираємо стандартний border */
  border-radius: 4px;
  cursor: pointer;
  margin-top: 10px;
  margin-right: 10px;
  box-shadow: ${props => props.outline ? 'inset 0 0 0 2px #a13b16' : 'none'};
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const SectionTitle = styled.h2`
  margin: 30px 0 15px 0;
  font-family: 'IBM Plex Mono', monospace;
  color: #a13b16;
`;

const ModalContent = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ItemCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border: 1px solid #ddd;
  border-radius: 12px;
  background: #fafafa;
  font-family: "IBM Plex Mono", monospace;
  margin-bottom: 10px;
`;

const ItemImage = styled.img`
  width: 50px;
  height: 70px;
  object-fit: cover;
  border-radius: 6px;
`;

const ItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const ItemTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
`;

const ItemQuantity = styled.span`
  font-size: 14px;
`;

const ItemPrice = styled.div`
  font-weight: 700;
  font-size: 16px;
  color: #3b6255;
`;

interface Order {
  order_id: number;
  customer_id: number;
  order_status: boolean;
  payment_status: boolean;
  order_price: number;
  phone: string;
  delivery_type: string;
  delivery_info: string;
  payment_method: string;
  order_date: string;
}

interface OrderItem {
  book_id: number;
  book_quantity: number;
  book_title: string;
  book_price: number;
  book_cover_path?: string;
  author_id: number;
}

const OrdersListView: React.FC = () => {
  const [pendingOrders, setPendingOrders] = useState<Order[]>([]);
  const [processingOrders, setProcessingOrders] = useState<Order[]>([]);
  const [completedOrders, setCompletedOrders] = useState<Order[]>([]);
  const [userNames, setUserNames] = useState<Record<number, string>>({});
  const [modalOrder, setModalOrder] = useState<Order | null>(null);
  const [modalItems, setModalItems] = useState<OrderItem[]>([]);
  const { accessToken } = useSelector((state: RootState) => state.auth);

  const fetchFullNames = async (orders: Order[]) => {
    const newNames: Record<number, string> = {};
    await Promise.all(
      orders.map(async (order) => {
        const res = await fetch(`${process.env.REACT_APP_BASE_API_URL}/profile/${order.customer_id}/fullname`, {
          credentials: 'include',
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const data = await res.json();
        newNames[order.customer_id] = data.fullname || `User ${order.customer_id}`;
      })
    );
    setUserNames(prev => ({ ...prev, ...newNames }));
  };

  const fetchOrders = async () => {
    try {
      const pendingRes = await fetch(`${process.env.REACT_APP_BASE_API_URL}/order/payment-pending`, { 
        credentials: 'include',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const pendingData = await pendingRes.json();
      setPendingOrders(pendingData);

      const processingRes = await fetch(`${process.env.REACT_APP_BASE_API_URL}/order/payment-completed-or-after-delivery`, { 
        credentials: 'include',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const processingData = await processingRes.json();
      setProcessingOrders(processingData);

      const completedRes = await fetch(`${process.env.REACT_APP_BASE_API_URL}/order/completed`, { 
        credentials: 'include',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const completedData = await completedRes.json();
      setCompletedOrders(completedData);

      fetchFullNames([...pendingData, ...processingData, ...completedData]);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrderItems = async (orderId: string) => {
    const res = await fetch(`${process.env.REACT_APP_BASE_API_URL}/order/${orderId}/items`, {
      credentials: 'include',
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const data = await res.json();
    return data;
  };

  const handleShowMore = async (order: Order) => {
    const items = await fetchOrderItems(order.order_id.toString());
    setModalOrder(order);
    setModalItems(items);
  };

  const handleCloseModal = () => {
    setModalOrder(null);
    setModalItems([]);
  };

  const handleConfirmPayment = async (orderId: number) => {
    await fetch(`${process.env.REACT_APP_BASE_API_URL}/order/set-payment-true/${orderId}`, { 
      method: 'POST', 
      credentials: 'include',
      headers: { 'Authorization': `Bearer ${accessToken}` },
    });
    const order = pendingOrders.find(o => o.order_id === orderId);
    if (order) {
      setProcessingOrders(prev => [order, ...prev]);
      setPendingOrders(prev => prev.filter(o => o.order_id !== orderId));
    }
  };

  const handleProcessOrder = async (orderId: number) => {
    await fetch(`${process.env.REACT_APP_BASE_API_URL}/order/set-order-true/${orderId}`, { 
      method: 'POST', 
      credentials: 'include',
      headers: { 'Authorization': `Bearer ${accessToken}` },
    });
    const order = processingOrders.find(o => o.order_id === orderId);
    if (order) {
      setCompletedOrders(prev => [order, ...prev]);
      setProcessingOrders(prev => prev.filter(o => o.order_id !== orderId));
    }
  };

  const formatDeliveryInfo = (deliveryType: string, deliveryInfo: string) => {
    try {
      const info = JSON.parse(deliveryInfo);
      if (deliveryType === 'courier') {
        return {
          address: info.address || '',
          date: info.date || '',
          interval: info.interval || '',
        };
      } else {
        return {
          address: info.address || '',
          date: '',
          interval: '',
        };
      }
    } catch {
      return {
        address: deliveryInfo,
        date: '',
        interval: '',
      };
    }
  };

  return (
    <PageContainer>
      <Title>Замовлення</Title>

      <SectionTitle>Очікують оплату</SectionTitle>
      <HorizontalScroll>
        {pendingOrders.map(order => (
          <OrderCard key={order.order_id} $processed={false}>
            <OrderField><strong>ID:</strong> {order.order_id}</OrderField>
            <OrderField><strong>Клієнт:</strong> {userNames[order.customer_id] || order.customer_id}</OrderField>
            <OrderField><strong>Сума:</strong> {order.order_price} грн</OrderField>
            <OrderField><strong>Телефон:</strong> {order.phone}</OrderField>
            <OrderField><strong>Доставка:</strong> {order.delivery_type === 'courier' ? 'Кур’єрська доставка' : 'Нова пошта'}</OrderField>
            <OrderField>
              <strong>Дата замовлення: </strong>
              {new Date(order.order_date).toLocaleDateString("uk-UA", {
                year: "numeric",
                month: "numeric",
                day: "numeric"
              })}
              ,{" "}
              {new Date(order.order_date).toLocaleTimeString("uk-UA", {
                hour: "2-digit",
                minute: "2-digit"
              })}
            </OrderField>
            <div>
              <ProcessButton onClick={() => handleConfirmPayment(order.order_id)}>Підтвердити оплату</ProcessButton>
              <ProcessButton outline onClick={() => handleShowMore(order)}>Більше...</ProcessButton>
            </div>
          </OrderCard>
        ))}
      </HorizontalScroll>

      <SectionTitle>Оплата підтверджена / Післяплата</SectionTitle>
      <Grid>
        {processingOrders.map(order => (
          <OrderCard key={order.order_id} $processed={order.order_status}>
            <OrderField><strong>ID:</strong> {order.order_id}</OrderField>
            <OrderField><strong>Клієнт:</strong> {userNames[order.customer_id] || order.customer_id}</OrderField>
            <OrderField><strong>Сума:</strong> {order.order_price} грн</OrderField>
            <OrderField><strong>Телефон:</strong> {order.phone}</OrderField>
            <OrderField><strong>Доставка:</strong> {order.delivery_type === 'courier' ? 'Кур’єрська доставка' : 'Нова пошта'}</OrderField>
            <OrderField><strong>Статус:</strong> {order.order_status ? 'Опрацьоване' : 'Очікує опрацювання'}</OrderField>
            <OrderField>
              <strong>Дата замовлення: </strong>
              {new Date(order.order_date).toLocaleDateString("uk-UA", {
                year: "numeric",
                month: "numeric",
                day: "numeric"
              })}
              ,{" "}
              {new Date(order.order_date).toLocaleTimeString("uk-UA", {
                hour: "2-digit",
                minute: "2-digit"
              })}
            </OrderField>
            <div>
              {!order.order_status && (
                <ProcessButton onClick={() => handleProcessOrder(order.order_id)}>Опрацювати</ProcessButton>
              )}
              <ProcessButton outline onClick={() => handleShowMore(order)}>Більше...</ProcessButton>
            </div>
          </OrderCard>
        ))}
      </Grid>

      <SectionTitle>Завершені</SectionTitle>
      <HorizontalScroll>
        {completedOrders.map(order => (
          <OrderCard key={order.order_id} $processed={true}>
            <OrderField><strong>ID:</strong> {order.order_id}</OrderField>
            <OrderField><strong>Клієнт:</strong> {userNames[order.customer_id] || order.customer_id}</OrderField>
            <OrderField><strong>Сума:</strong> {order.order_price} грн</OrderField>
            <OrderField>
              <strong>Дата замовлення: </strong>
              {new Date(order.order_date).toLocaleDateString("uk-UA", {
                year: "numeric",
                month: "numeric",
                day: "numeric"
              })}
              ,{" "}
              {new Date(order.order_date).toLocaleTimeString("uk-UA", {
                hour: "2-digit",
                minute: "2-digit"
              })}
            </OrderField>
            <ProcessButton outline onClick={() => handleShowMore(order)}>Більше...</ProcessButton>
          </OrderCard>
        ))}
      </HorizontalScroll>

      {modalOrder && (
        <ModalOverlay onClick={handleCloseModal}>
          <ModalContent onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
            <h3>Замовлення #{modalOrder.order_id}</h3>
            <p><strong>Клієнт:</strong> {userNames[modalOrder.customer_id]}</p>
            <p><strong>Телефон:</strong> {modalOrder.phone}</p>
            {(() => {
              const info = formatDeliveryInfo(modalOrder.delivery_type, modalOrder.delivery_info);
              return (
                <>
                  <p><strong>Адреса:</strong> {info.address}</p>
                </>
              );
            })()}
            <p><strong>Метод оплати:</strong> {modalOrder.payment_method === 'card' ? 'Картка' : 'Післяплата'}</p>
            <h4>Товари:</h4>
            {modalItems.map(item => (
              <ItemCard key={item.book_id}>
                <ItemImage src={item.book_cover_path || "/placeholder.png"} alt={item.book_title} />
                <ItemInfo>
                  <ItemTitle>{item.book_title}</ItemTitle>
                  <ItemQuantity>x{item.book_quantity}</ItemQuantity>
                </ItemInfo>
                <ItemPrice>{item.book_price} грн</ItemPrice>
              </ItemCard>
            ))}
            <ProcessButton onClick={handleCloseModal}>Закрити</ProcessButton>
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

export default OrdersListView;
